import { useLocations } from '@my/directory-api/hooks';
import type { Address } from '@my/directory-api';
import type { CargoDefinition, Order, OrderTask, OrderTemplate, ServiceTag } from '@my/tms-api';
import {
  useArchiveOrderTemplate,
  useCargoDefinitions,
  useDeleteOrderTemplate,
  useOrderTemplates,
  useServiceTags,
  useUpdateOrderTemplate,
} from '@my/tms-api/hooks';
import { ConfirmDialog, ErrorBanner, Loading, cn, showToast } from '@my/ui';
import { Archive, ArchiveRestore, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { RowActionsMenu } from '@/components/RowActionsMenu';
import { CargoCell, RouteStrip } from './OrderListCells';
import { TemplateNameDialog } from './OrderTemplateDialogs';

/**
 * New order → "Start from a template": the customer's order templates, most
 * used first, beside the panel that builds the order from the one picked.
 * Each row's "⋮" renames, archives (restorable) or deletes it.
 *
 * <p>
 * Picking a cargo on New order narrows the list to that cargo, as the previous
 * orders list does.
 * </p>
 */
export function OrderTemplatesPanel({
  organisationId,
  customerPartyId,
  customerName,
  cargoDefinitionId,
  cargoVariantId,
  selectedTemplateId,
  onSelect,
  headerAside,
}: {
  organisationId: string | null;
  customerPartyId: string;
  customerName: string;
  cargoDefinitionId: string | null;
  cargoVariantId: string | null;
  selectedTemplateId: string | null;
  onSelect: (id: string | null) => void;
  /** The Previous orders | Templates switch. */
  headerAside?: ReactNode;
}) {
  const [showArchived, setShowArchived] = useState(false);
  const query = useOrderTemplates(organisationId, customerPartyId, showArchived);
  const cargoDefsQuery = useCargoDefinitions(organisationId);
  const serviceTagsQuery = useServiceTags(organisationId);
  const locationsQuery = useLocations(organisationId);

  const templates = useMemo(
    () =>
      (query.data ?? []).filter(
        (t) =>
          (!cargoDefinitionId || t.cargoDefinitionId === cargoDefinitionId) &&
          (!cargoVariantId || t.cargoDefinitionVariantId === cargoVariantId),
      ),
    [query.data, cargoDefinitionId, cargoVariantId],
  );
  const activeCount = templates.filter((t) => t.status === 'Active').length;

  const cargoDefsById = useMemo(
    () => new Map<string, CargoDefinition>((cargoDefsQuery.data ?? []).map((d) => [d.id, d])),
    [cargoDefsQuery.data],
  );
  const serviceTagsById = useMemo(
    () => new Map<string, ServiceTag>((serviceTagsQuery.data ?? []).map((t) => [t.id, t])),
    [serviceTagsQuery.data],
  );
  const addressById = useMemo(
    () => new Map<string, Address>((locationsQuery.data ?? []).map((a) => [a.id, a])),
    [locationsQuery.data],
  );

  // Keep a usable selection: the first active template, or none.
  useEffect(() => {
    if (!query.isSuccess) return;
    const usable = templates.filter((t) => t.status === 'Active');
    if (usable.length === 0) {
      if (selectedTemplateId !== null) onSelect(null);
      return;
    }
    if (!usable.some((t) => t.id === selectedTemplateId)) onSelect(usable[0]!.id);
  }, [query.isSuccess, templates, selectedTemplateId, onSelect]);

  const [renaming, setRenaming] = useState<OrderTemplate | null>(null);
  const [deleting, setDeleting] = useState<OrderTemplate | null>(null);
  const update = useUpdateOrderTemplate();
  const archive = useArchiveOrderTemplate();
  const remove = useDeleteOrderTemplate();
  const fail = (err: unknown, fallback: string) =>
    showToast({ type: 'error', message: err instanceof Error ? err.message : fallback });

  return (
    <section className="flex h-full flex-col rounded-md border border-slate-200 bg-white">
      <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-3 py-2">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">
            Start from a template{' '}
            {query.isSuccess && <span className="text-slate-400">({activeCount})</span>}
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            {customerName}’s templates, most used first. Save one from any order with “Save as
            template”.
          </p>
        </div>
        {headerAside}
      </header>

      {query.isPending && (
        <div className="p-3">
          <Loading label="Loading templates" />
        </div>
      )}
      {query.isError && (
        <div className="p-3">
          <ErrorBanner
            title="Couldn’t load templates"
            message={query.error?.message ?? 'Unknown error'}
          />
        </div>
      )}
      {query.isSuccess && templates.length === 0 && (
        <p className="px-3 py-6 text-center text-xs italic text-slate-400">
          No templates for {customerName} yet — open one of their orders and use “Save as template”.
        </p>
      )}

      {query.isSuccess && templates.length > 0 && (
        <div className="max-h-[18rem] min-h-0 flex-1 overflow-auto xl:max-h-none">
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2 text-left font-semibold">Template</th>
                <th className="px-3 py-2 text-left font-semibold">Cargo</th>
                <th className="px-3 py-2 text-left font-semibold">Route</th>
                <th className="whitespace-nowrap px-3 py-2 text-right font-semibold">Tasks</th>
                <th className="w-8 px-1 py-2" aria-label="Actions" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {templates.map((t) => {
                const archived = t.status === 'Archived';
                const selected = t.id === selectedTemplateId;
                // The shared cells read an order and its tasks; a template's
                // cargo and stops are enough for both.
                const asOrder = {
                  cargoDefinitionVariantId: t.cargoDefinitionVariantId,
                  serviceTagIds: t.serviceTagIds,
                } as unknown as Order;
                const stops = t.stops.map(
                  (s) =>
                    ({
                      id: `${t.id}-${s.sequence}`,
                      sequence: s.sequence,
                      type: s.type,
                      addressId: s.addressId,
                      state: 'Pending',
                    }) as unknown as OrderTask,
                );
                return (
                  <tr
                    key={t.id}
                    tabIndex={archived ? -1 : 0}
                    aria-selected={selected}
                    onClick={() => !archived && onSelect(t.id)}
                    onKeyDown={(e) => {
                      if (!archived && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        onSelect(t.id);
                      }
                    }}
                    className={cn(
                      'align-top outline-none focus-visible:bg-slate-50',
                      archived ? 'bg-slate-50/60 text-slate-400' : 'cursor-pointer',
                      selected ? 'bg-blue-50' : !archived && 'hover:bg-slate-50',
                    )}
                  >
                    <td
                      className={cn(
                        'border-l-2 px-3 py-2.5',
                        selected ? 'border-l-blue-500' : 'border-l-transparent',
                      )}
                    >
                      <div className="space-y-0.5">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-xs font-semibold text-slate-900">{t.name}</span>
                          {archived && (
                            <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                              Archived
                            </span>
                          )}
                        </div>
                        {t.description && (
                          <p className="max-w-[18rem] text-[11px] text-slate-500">
                            {t.description}
                          </p>
                        )}
                        <p className="text-[10px] text-slate-400">
                          {t.timesUsed === 0
                            ? 'Not used yet'
                            : `Used ${t.timesUsed}×${t.lastUsedUtc ? ` · last ${shortDate(t.lastUsedUtc)}` : ''}`}
                        </p>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <CargoCell
                        order={asOrder}
                        cargoDef={cargoDefsById.get(t.cargoDefinitionId)}
                        serviceTagsById={serviceTagsById}
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <RouteStrip tasks={stops} addressById={addressById} pending={false} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right text-xs font-semibold text-slate-700">
                      {t.stops.length}
                    </td>
                    <td className="px-1 py-2" onClick={(e) => e.stopPropagation()}>
                      <RowActionsMenu
                        label={t.name}
                        actions={[
                          {
                            id: 'rename',
                            label: 'Rename…',
                            icon: <Pencil className="h-3.5 w-3.5" />,
                            onSelect: () => setRenaming(t),
                          },
                          archived
                            ? {
                                id: 'restore',
                                label: 'Restore',
                                icon: <ArchiveRestore className="h-3.5 w-3.5" />,
                                onSelect: () =>
                                  archive.mutate(
                                    { id: t.id, archive: false },
                                    { onError: (e) => fail(e, 'Couldn’t restore the template.') },
                                  ),
                              }
                            : {
                                id: 'archive',
                                label: 'Archive',
                                icon: <Archive className="h-3.5 w-3.5" />,
                                onSelect: () =>
                                  archive.mutate(
                                    { id: t.id, archive: true },
                                    {
                                      onSuccess: () =>
                                        showToast({
                                          type: 'success',
                                          message: `“${t.name}” archived — “Show archived” brings it back.`,
                                        }),
                                      onError: (e) => fail(e, 'Couldn’t archive the template.'),
                                    },
                                  ),
                              },
                          {
                            id: 'delete',
                            label: 'Delete',
                            icon: <Trash2 className="h-3.5 w-3.5" />,
                            destructive: true,
                            onSelect: () => setDeleting(t),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {query.isSuccess && (
        <div className="border-t border-slate-100 px-3 py-1.5 text-right">
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            className="rounded-sm px-2 py-0.5 text-[11px] font-medium text-purple-600 hover:bg-purple-50"
          >
            {showArchived ? 'Hide archived' : 'Show archived'}
          </button>
        </div>
      )}

      <TemplateNameDialog
        open={renaming !== null}
        title="Rename template"
        initialName={renaming?.name ?? ''}
        initialDescription={renaming?.description ?? ''}
        confirmLabel="Save"
        busy={update.isPending}
        onClose={() => setRenaming(null)}
        onConfirm={(name, description) =>
          renaming &&
          update.mutate(
            { id: renaming.id, name, description },
            {
              onSuccess: () => setRenaming(null),
              onError: (e) => fail(e, 'Couldn’t rename the template.'),
            },
          )
        }
      />

      <ConfirmDialog
        open={deleting !== null}
        title="Delete template?"
        message={
          deleting
            ? `“${deleting.name}” will be removed for good. Orders already made from it are not affected. Archive it instead to keep it out of the way.`
            : undefined
        }
        confirmLabel="Delete"
        tone="danger"
        busy={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() =>
          deleting &&
          remove.mutate(
            { id: deleting.id },
            {
              onSuccess: () => {
                if (deleting.id === selectedTemplateId) onSelect(null);
                setDeleting(null);
              },
              onError: (e) => fail(e, 'Couldn’t delete the template.'),
            },
          )
        }
      />
    </section>
  );
}

/** "12 Oct". */
function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
